import { Link } from "@tanstack/react-router"
import { Archive, DocumentFilter, SearchNormal1 } from "iconsax-reactjs"
import { parseAsString, useQueryStates } from "nuqs"
import { useMemo, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import PullToRefresh from "react-simple-pull-to-refresh"

import { LoadingSpinner } from "@/components/shared"
import { getAffiliateExplore } from "@/features/jobs/affiliates/api/affiliate.api"
import { Filter, FilterTabs } from "@/features/jobs/affiliates/components/affiliate-filter"
import {
  AffiliateItem,
  AffiliateItemSkeleton
} from "@/features/jobs/affiliates/components/affiliate-item"
import { CategoryFilter } from "@/features/jobs/affiliates/components/category-filter"
import { EFilterAffiliate } from "@/modules/helpers/enum"
import { useInfiniteScrollQuery } from "@/modules/helpers/hooks"
import { transformFilterToQueryParams,useFilterStore } from "@/modules/helpers/hooks/shared"
import { sendHaptic } from "@/modules/helpers/utils"
import type { Affiliate } from "@/modules/types"

// Constants
const SKELETON_COUNT = 5
const GRID_COLUMNS = "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"

interface AffiliateListProps {
  data: Affiliate[] | undefined
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  onLoadMore: () => void
}

// Components

const SearchHeader = ({ refetch }: { refetch: () => void }) => {
  const [isVisible, setIsVisible] = useState(false)
  const { countFilter } = useFilterStore()

  return (
    <>
      <div className="z-10 gap-2 px-2 flex flex-col sticky top-0">
        <div className="flex justify-between items-center gap-2 py-1">
          <Link
            to="/s/affiliate"
            onClick={() => sendHaptic()}
            className="rounded-xl transition-all ease-in-out h-12 z-40 lg:w-[240px] flex-1 gap-2 items-center px-3 flex border border-gray-200 bg-white"
          >
            <SearchNormal1 size={20} color="#045664" />
            <p className="text-xs font-light text-[rgba(130,_146,_170,_1)]">Tìm kiếm sản phẩm</p>
          </Link>
          <div
            className="rounded-xl w-12 h-12 border flex justify-center items-center bg-white relative cursor-pointer"
            onClick={() => {
              setIsVisible(true)
              sendHaptic()
            }}
          >
            {countFilter > 0 && (
              <div className="bg-red-500 text-white flex justify-center items-center text-[10px] font-medium w-4 h-4 rounded-full absolute -top-1 -right-1">
                {countFilter}
              </div>
            )}
            <DocumentFilter size={24} className="text-primary-800" />
          </div>
          <Link
            to="/affiliate/saved"
            className="rounded-xl w-12 h-12 border flex justify-center items-center bg-white"
          >
            <Archive size={24} variant="Bold" className="text-primary-800" />
          </Link>
        </div>
      </div>
      <Filter refetch={refetch} isVisible={isVisible} setIsVisible={setIsVisible} />
    </>
  )
}

const AffiliateSkeleton: React.FC = () => (
  <div className={`${GRID_COLUMNS} grid lg:mr-auto pb-10 gap-4 mt-4`}>
    {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
      <AffiliateItemSkeleton key={index} />
    ))}
  </div>
)

const EmptyState: React.FC = () => (
  <div className="h-36 flex justify-center w-full items-center">
    <p className="text-xs text-gray-400">Không có sản phẩm</p>
  </div>
)

const AffiliateList: React.FC<AffiliateListProps> = ({
  data,
  isLoading,
  hasNextPage,
  onLoadMore
}) => {
  if (isLoading) {
    return <AffiliateSkeleton />
  }

  if (!data?.length) {
    return <EmptyState />
  }

  return (
    <InfiniteScroll
      dataLength={data.length}
      next={onLoadMore}
      hasMore={hasNextPage}
      scrollableTarget="scrollable"
      loader={<LoadingSpinner className="w-20 h-20" />}
    >
      <div className="grid grid-cols-2 gap-2 py-4">
        {data.map((item: Affiliate) => (
          <AffiliateItem key={item.id} data={item} />
        ))}
      </div>
    </InfiniteScroll>
  )
}

// Main Component
const AffiliatePage = () => {
  // Query states
  const [searchParams, setSearchParams] = useQueryStates({
    filterBy: parseAsString.withDefault(EFilterAffiliate.SUITABLE),
    category: parseAsString.withDefault(""),
    search: parseAsString.withDefault("")
  })
  const { filterState } = useFilterStore()

  // Transform filter state to API query params
  const apiParams = useMemo(() => {
    const transformed = transformFilterToQueryParams(filterState)
    return {
      filterBy: searchParams.filterBy || undefined,
      category: searchParams.category || undefined,
      search: searchParams.search || undefined,
      ...transformed
    }
  }, [filterState, searchParams.filterBy, searchParams.category, searchParams.search])

  // Data fetching
  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, forceRefetch } =
    useInfiniteScrollQuery<unknown, Affiliate>({
      queryKey: [
        "affiliate",
        searchParams.filterBy,
        searchParams.category,
        searchParams.search,
        JSON.stringify(filterState)
      ],
      queryFn: getAffiliateExplore,
      params: apiParams
    })

  // Event handlers
  const handleRefresh = async () => {
    await forceRefetch()
  }

  const handleFilterChange = (filter: string) => {
    if (filter === searchParams.filterBy) setSearchParams({ filterBy: "" })
    else setSearchParams({ filterBy: filter })
  }

  const handleCategoryChange = (category: string | null) => {
    setSearchParams({ category: category || "" })
  }

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }

  return (
    <>
      {/* Header */}

      <SearchHeader refetch={forceRefetch} />

      <CategoryFilter value={searchParams.category} onChange={handleCategoryChange} />

      <FilterTabs
        refetch={forceRefetch}
        onChange={handleFilterChange}
        onChangeCategory={handleCategoryChange}
        category={searchParams.category}
        value={searchParams.filterBy}
      />
      {/* Filter */}
      <div className="px-2">
        {/* Content */}{" "}
        <PullToRefresh
          onRefresh={handleRefresh}
          pullingContent=""
          refreshingContent={<LoadingSpinner className="w-20 h-20" />}
          maxPullDownDistance={80}
        >
          <div className="flex-1 flex flex-col">
            <AffiliateList
              data={data?.list}
              isLoading={isLoading}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              onLoadMore={handleLoadMore}
            />
          </div>
        </PullToRefresh>
      </div>
    </>
  )
}

export default AffiliatePage
